import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useSearchParams } from "react-router-dom";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import Card from "../layout/Card";
import Filters from "../layout/Filters";
import { translateText } from "../../utils/translation";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import LineChart from "../charts/line/LineChart";
import DonutChart from "../charts/radial/DonutChart";
import { getCommunication } from "../../slices/oversight";
import VerticalBarChart from "../charts/bar/VerticalBarChart";
import { getColours } from "../../utils/colours";

export default function OversightCommunication() {
  const dispatch = useAppDispatch();

  // Get query params
  const [searchParams] = useSearchParams();
  const dateRangeTerm = searchParams.get("dateRange");
  const searchProperty = searchParams.get("property");

  // Fetch data from server
  useEffect(() => {
    dispatch(
      getCommunication({
        dateRange: dateRangeTerm,
        propertyId: searchProperty,
      }),
    );
  }, [dispatch, dateRangeTerm, searchProperty]);

  // Set state
  const [property, setProperty] = useState({ value: "" });
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { data, loading } = useAppSelector((state) => state.oversight);
  const language = user === null ? "EN" : user.language;

  const propertyChoices =
    user === null
      ? []
      : user.scope.map((x: any) => {
          return {
            value: x.id,
            label: x.name,
          };
        });

  function formatDataForCharts() {
    let obj: { [key: string]: any } = {
      timeSeries: [],
      categoryCount: [],
      propertyCount: [],
      roomCount: [],
    };
    if (data === null) return obj;

    // Build time series
    obj.timeSeries = data.time_series.map((x: any) => {
      return {
        id: x.property.name,
        data: x.data.map((y: any) => {
          return {
            x: y.hour,
            y: y.count,
          };
        }),
      };
    });

    // Build property count
    obj.propertyCount = data.property_count.map((x: any) => {
      return {
        id: x.property.name,
        label: x.property.name,
        Count: x.count,
      };
    });

    // Build room count
    obj.roomCount = data.room_count.map((x: any) => {
      return {
        id: `${x.property.name} - ${x.room.name}`,
        label: `${x.property.name} - ${x.room.name}`,
        value: x.count,
      };
    });

    return obj;
  }

  // Constant data
  const formattedData = formatDataForCharts();

  return (
    <Template>
      {/* Header */}
      <Header text={translateText("communication", language)} />

      {/* Loading spinner */}
      {loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            <Filters
              language={language}
              className="pb-10"
              filters={[
                {
                  id: "property",
                  name: "Property",
                  options: propertyChoices,
                },
              ]}
              queryParams={[
                ["property", searchProperty],
                ["dateRange", dateRangeTerm],
              ]}
              handlers={{
                property: {
                  state: property,
                  stateName: "value",
                  onChange: setProperty,
                },
                dateRange: {
                  state: dateRange,
                  stateName: "N/A",
                  onChange: setDateRange,
                },
              }}
              loading={loading}
              baseUrl="/oversight/communication"
              showDateRange={true}
              showFilters={true}
              showSearchBar={false}
            />
            {data !== null ? (
              <div className="mx-1 mb-10">
                {/* Row 1 */}
                <div className="grid grid-cols-1 mt-6 md:space-x-8 space-x-0 md:space-y-0 space-y-8">
                  {/* Property time series */}
                  <Card className="overflow-x-auto scrollbar-invisible">
                    <div className="flex w-full items-center justify-between space-x-6">
                      <div className="flex-1">
                        <div className="border-b border-gray-200 px-4 pb-4 pt-0 mb-4 sm:px-6">
                          <h3 className="text-base font-semibold leading-6 text-black">
                            {translateText(
                              "announcement_time_series",
                              language,
                            )}
                          </h3>
                          <span className="text-xs text-secondary">
                            {translateText(
                              "oversight_communication_time_series_subtext",
                              language,
                            )}
                          </span>
                        </div>
                        <LineChart
                          data={formattedData.timeSeries}
                          className="mx-auto text-sm lg:h-72 h-64"
                          colours={getColours("multi-time-series-chart")}
                          margins={{
                            top: 20,
                            right: 120,
                            bottom: 20,
                            left: 40,
                          }}
                          showLegend={true}
                        />
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Row 2 */}
                <div className="grid md:grid-cols-2 grid-cols-1 mt-6 md:space-x-8 space-x-0 md:space-y-0 space-y-8">
                  {/* Property count */}
                  <Card className="overflow-x-auto scrollbar-invisible">
                    <div className="flex w-full items-center justify-between space-x-6">
                      <div className="flex-1">
                        <div className="border-b border-gray-200 px-4 pb-4 pt-0 mb-4 sm:px-6">
                          <h3 className="text-base font-semibold leading-6 text-black">
                            {translateText("property_count", language)}
                          </h3>
                          <span className="text-xs text-secondary">
                            {translateText(
                              formattedData.propertyCount.length === 0
                                ? "not_enough_data"
                                : "oversight_communication_property_count_subtext",
                              language,
                            )}
                          </span>
                        </div>
                        <VerticalBarChart
                          data={formattedData.propertyCount}
                          className="mx-auto text-sm lg:h-64 h-48"
                          keys={
                            formattedData.propertyCount.length === 0
                              ? ["value"]
                              : ["Count"]
                          }
                        />
                      </div>
                    </div>
                  </Card>

                  {/* Room count */}
                  <Card className="overflow-x-auto scrollbar-invisible">
                    <div className="flex w-full items-center justify-between space-x-6">
                      <div className="flex-1">
                        <div className="border-b border-gray-200 px-4 pb-4 pt-0 mb-4 sm:px-6">
                          <h3 className="text-base font-semibold leading-6 text-black">
                            {translateText("room_count", language)}
                          </h3>
                          <span className="text-xs text-secondary">
                            {translateText(
                              formattedData.roomCount.length === 0
                                ? "not_enough_data"
                                : "oversight_communication_room_count_subtext",
                              language,
                            )}
                          </span>
                        </div>
                        <DonutChart
                          data={formattedData.roomCount}
                          className="mx-auto text-sm lg:h-64 h-48"
                        />
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("not_enough_data", language)}
                subtext={translateText(
                  "not_enough_data_analytics_subtext",
                  language,
                )}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
